import { parseResponse } from '$lib/client/responseHelpers';
import Emitter from '$lib/services/streaming/eventsDispatcher';
import Events from '$lib/services/streaming/eventsDispatcher/events';
import { StreamingStatsEvents } from '$lib/services/streaming/statistics';
import type {
    BroadcastConfig,
    CloudFlarePlayerConfig,
    PlayerConfigCommon,
    RTCPeerConnectionExtended,
    StreamingProvider,
} from '$lib/services/streaming/types';
import { WebRTCPlayer } from '$lib/services/streaming/WebRTC/WHEPClient/main';
import type { WebRTCPlayerType } from '$lib/services/streaming/WebRTC/WHEPClient/types';
import WHIPClient from '$lib/services/streaming/WebRTC/WHIPClient/WHIPClient';

import { sendEventLog } from '@api/room/room';
import { getStreamConfig, regenerateStreamConfig } from '@api/streaming/streaming';
import { networkInformationStore } from '@components/room/no-internet-state/store';
import { streamingLoggerStore } from '@components/room/streaming-debugger/stores/logger';
import { LogType } from '@shared/types';
import { PlayerStatus, StreamingType, StreamProvider, StreamStatus } from '@shared/types/room';
import { isHttpError } from '@sveltejs/kit';

export default class CloudflareStreaming implements StreamingProvider {
    broadcastPeerConnection: RTCPeerConnectionExtended | undefined;
    broadcast: WHIPClient | undefined;
    player: WebRTCPlayerType | undefined;
    timer: ReturnType<typeof setTimeout> | undefined;
    broadcastCheckTimer?: number;
    deliveryCheckTimer?: number;
    playerPeerConnectionStartedTimeout: ReturnType<typeof setTimeout> | undefined;
    deAttachBroadcastPeerConnectionEvents?: () => void;
    private needRegenerateConfig = false;

    broadCastConfig?: BroadcastConfig;

    public getType() {
        return StreamProvider.CloudFlare;
    }

    public async startBroadcast(config: BroadcastConfig) {
        this.broadCastConfig = config;
        this._startBroadcast();
    }

    private async _startBroadcast() {
        if (!this.broadCastConfig) {
            console.error('No broadcast config');
            return;
        }
        const { mediaStream, onEvent } = this.broadCastConfig;

        streamingLoggerStore.log(
            { type: LogType.AppBroadcast, message: 'StreamingProvider: CloudflareStreaming: getConfig' }
        );
        let publishUrl: string | null | undefined;
        if (this.needRegenerateConfig) {
            streamingLoggerStore.log({
                type: LogType.AppBroadcast,
                message: 'StreamingProvider: CloudflareStreaming: regenerate config',
            });
            publishUrl = await this.regenerateConfig();
            this.needRegenerateConfig = false;
        } else {
            publishUrl = await this.getBroadcastConfigWithRetry();
        }
        if (!publishUrl) {
            streamingLoggerStore.log(
                { type: LogType.AppBroadcast, message: 'StreamingProvider: CloudflareStreaming: getConfig: Error' }
            );
            console.error('CloudflareStreaming: getConfig: Error');
            onEvent(StreamStatus.Error);
            return;
        }

        streamingLoggerStore.log(
            { type: LogType.AppBroadcast, message: 'StreamingProvider: CloudflareStreaming: startBroadcast' },
            { publishUrl: publishUrl },
            { includeExtendedData: true }
        );

        this.broadcast = new WHIPClient(
            publishUrl,
            mediaStream,
            this.broadCastConfig.reduceBitrate,
            (needRegen = false) => {
                this.needRegenerateConfig = needRegen;
                onEvent(StreamStatus.Error);
            }
        );
        this.broadcastPeerConnection = this.broadcast.peer;

        this.deAttachBroadcastPeerConnectionEvents = this.attachBroadcastPeerConnectionEvents(onEvent);
        this.monitorBroadcastStats(onEvent);
        return this.broadcast;
    }

    private async getBroadcastConfigWithRetry() {
        let retryCount = 3;
        while (retryCount) {
            const publishUrl = await this.getBroadcastConfig();
            if (publishUrl) {
                return publishUrl;
            }
            console.error('Error to get CloudFlare broadcast config: retry.');
            retryCount -= 1;
            await new Promise<void>((res) => setTimeout(res, 1000));
        }
        this.statsBroadcastCallback(
            'rtc_cf_broadcast_config_error',
            { errorText: 'Retry exceed' }
        );
        console.error('getConfigWithRetry: retry exceed');
        return null;
    }

    private async getBroadcastConfig() {
        const startRequest = performance.now();
        try {
            const configResp = await getStreamConfig();
            const data = parseResponse(configResp);
            if (!data) {
                this.statsBroadcastCallback(
                    'rtc_cf_broadcast_config_error',
                    { errorText: 'No config' }
                );
                console.error('No config');
                return null;
            }
            if (!data.cloudflare?.publishUrl) {
                this.statsBroadcastCallback(
                    'rtc_cf_broadcast_config_error',
                    { errorText: 'No cloudflare stream config' }
                );
                console.error('No cloudflare stream config');
                return null;
            }
            return data.cloudflare.publishUrl;
        } catch (error) {
            let errorText: string;
            if (isHttpError(error)) {
                errorText = error.body.message;
            } else {
                errorText = (error as Error).message;
            }
            this.statsBroadcastCallback(
                'rtc_cf_broadcast_config_error',
                { errorText }
            );
            sendEventLog(`CF broadcast fetch error: ${errorText}`, undefined, {
                processingTime: performance.now() - startRequest,
                message: (error as Error).message,
                name: (error as Error).name,
                errorType: (error as Error).constructor.name,
                isOnLine: navigator.onLine,
                ...networkInformationStore.get(),
            });
            console.error('Error to get cloudflare stream config', error);
            return null;
        }
    }

    private async regenerateConfig() {
        try {
            const configResp = await regenerateStreamConfig();
            const data = parseResponse(configResp);

            if (!data) {
                console.error('No config');
                return null;
            }
            if (!data.cloudflare?.publishUrl) {
                console.error('No cloudflare stream config');
                return null;
            }
            return data.cloudflare.publishUrl;
        } catch (error) {
            let errorText: string;
            if (isHttpError(error)) {
                errorText = error.body.message;
            } else {
                errorText = (error as Error).message;
            }
            this.statsBroadcastCallback(
                'rtc_cf_broadcast_regen_config_error',
                { errorText }
            );
            console.error('Error to regenerate cloudflare stream config', error);
            return null;
        }
    }

    private monitorBroadcastStats(onEvent:(event: StreamStatus | null) => void) {
        let lastVideoBytesSent = 0;
        let stalledCount = 0;
        const restartThreshold = 2;
        const checkInterval = 2_000;
        const monitor = async () => {
            console.log('Check');
            if (
                !this.broadcastPeerConnection ||
                this.broadcastPeerConnection.connectionState !== 'connected'
            ) {
                if (
                    !this.broadcastPeerConnection ||
                    ['new', 'connecting', 'connected']
                        .every((st) => this.broadcastPeerConnection?.connectionState !== st) ||
                    ['new', 'checking', 'connected', 'completed']
                        .every((st) => this.broadcastPeerConnection?.iceConnectionState !== st)
                ) {
                    console.log('RELOAD');
                    onEvent(StreamStatus.Error);
                }
                return;
            }
            try {
                const stats: RTCStatsReport = await this.broadcastPeerConnection.getStats();
                let videoReport: RTCOutboundRtpStreamStats | undefined = undefined;
                stats.forEach((report) => {
                    if (report.type === 'outbound-rtp' && report.kind === 'video') {
                        videoReport = report as RTCOutboundRtpStreamStats;
                    }
                });

                streamingLoggerStore.log({
                    type: LogType.AppBroadcast,
                    message: `CloudFlare: broadcast : videoReport: ${
                        videoReport ? JSON.stringify(videoReport) : undefined
                    }`,
                });

                if (videoReport) {
                    const videoStats = videoReport as RTCOutboundRtpStreamStats;
                    const currentBytesSent = videoStats.bytesSent;
                    if (currentBytesSent === undefined) {
                        return;
                    }
                    if (currentBytesSent === lastVideoBytesSent) {
                        stalledCount++;
                        streamingLoggerStore.log({
                            type: LogType.AppBroadcast,
                            message:
                                `StreamingProvider: CloudflareStreaming: stalled broadcast ${stalledCount} intervals`,
                        });
                        this.statsBroadcastCallback('rtc_stalled_frame', { errorText: '' });
                        if (stalledCount >= restartThreshold) {
                            streamingLoggerStore.log({
                                type: LogType.AppBroadcast,
                                message: 'StreamingProvider: CloudflareStreaming: stalled broadcast: restart',
                            });
                            this.statsBroadcastCallback('rtc_stalled_frame_restart', { errorText: '' });
                            onEvent(StreamStatus.Error);
                            return;
                        }
                    } else {
                        stalledCount = 0;
                    }
                    lastVideoBytesSent = currentBytesSent;
                }
            } catch (error) {
                console.error(error);
                streamingLoggerStore.log(
                    {
                        type: LogType.AppBroadcast,
                        message: `StreamingProvider: CloudflareStreaming: error to get stats`,
                    },
                    { errorText: (error as Error).message },
                    { includeExtendedData: true }
                );
            }
        };

        this.broadcastCheckTimer = window.setInterval(monitor, checkInterval);
    }

    public async restartBroadcast() {
        streamingLoggerStore.log({
            type: LogType.AppBroadcast,
            message: 'StreamingProvider: CloudflareStreaming: restartBroadcast',
        });
        this.stopBroadcast();
        await this._startBroadcast();
    }

    stopBroadcast() {
        if (this.broadcastCheckTimer) {
            clearInterval(this.broadcastCheckTimer);
        }
        if (this.broadcast) {
            streamingLoggerStore.log({
                type: LogType.AppBroadcast,
                message: 'StreamingProvider: CloudflareStreaming: stopBroadcast',
            });
            this.deAttachBroadcastPeerConnectionEvents &&
            this.deAttachBroadcastPeerConnectionEvents();

            Emitter.emit(Events.STREAMING_STATS_WEBRTC_UNWATCH_PEER_CONNECTION, this.broadcast.peer);
            Emitter.emit(
                Events.STREAMING_STATS_WEBRTC_SEND_EVENT,
                StreamingStatsEvents.stop_broadcast,
                { streamingType: 'broadcast' }
            );

            this.broadcast.stop();
        }
    }

    startCollectingPlayerStats(peerConnection: RTCPeerConnectionExtended) {
        const peer = peerConnection;

        peer.streamingType = StreamingType.Delivery;
        peer.connectionId = window.crypto.getRandomValues(new Uint32Array(1))?.[0] || 1;

        Emitter.emit(Events.STREAMING_STATS_SET_DELIVERY_START_TIMER);
        Emitter.emit(Events.STREAMING_STATS_WEBRTC_WATCH_PEER_CONNECTION, peer);

        clearTimeout(this.playerPeerConnectionStartedTimeout);
    }

    stopCollectingsPlayerStats() {
        clearTimeout(this.playerPeerConnectionStartedTimeout);
    }

    statsCallback(event: string, data: unknown) {
        const signalingState = this.player?.peer?.signalingState;
        const streamingType = 'delivery';

        Emitter.emit(Events.STREAMING_STATS_WEBRTC_SEND_EVENT, event, { ...data || {}, streamingType, signalingState });
    }

    statsBroadcastCallback(event: string, data: unknown) {
        const signalingState = this.broadcast?.peer?.signalingState;
        const streamingType = 'broadcast';

        Emitter.emit(Events.STREAMING_STATS_WEBRTC_SEND_EVENT, event, { ...data || {}, streamingType, signalingState });
    }

    async startDelivery(config: CloudFlarePlayerConfig & PlayerConfigCommon) {
        streamingLoggerStore.log(
            {
                type: LogType.AppDelivery,
                message: 'StreamingProvider: CloudflareStreaming: startDelivery',
            },
            config,
            { includeExtendedData: ['playUrl', 'streamId', 'streamOrigin'] }
        );
        this.player = new WebRTCPlayer({
            video: config.video,
            type: 'whep',
            statsTypeFilter: '^candidate-*|^inbound-rtp',
            debug: false,
            statsCallback: this.statsCallback.bind(this),
            startCollectingPlayerStats: this.startCollectingPlayerStats.bind(this),
        }) as unknown as WebRTCPlayerType;

        if (config.playUrl) {
            let RECONNECT_TIMEOUT = 1000;
            let RECONNECTS = 3;

            if (this.timer) {
                clearTimeout(this.timer);
            }
            this.player.on('initial-connection-failed', () => {
                streamingLoggerStore.log({
                    type: LogType.AppDelivery,
                    message: 'StreamingProvider: CloudflareStreaming: connection failed',
                });
                if (config.onEvent) {
                    config.onEvent(PlayerStatus.Error);
                }
            });

            this.player.on('connect-error', () => {
                streamingLoggerStore.log({
                    type: LogType.AppDelivery,
                    message: 'StreamingProvider: CloudflareStreaming: Connect error',
                });
                if (RECONNECTS <= 0) {
                    streamingLoggerStore.log({
                        type: LogType.AppDelivery,
                        message: 'StreamingProvider: CloudflareStreaming: Reconnect exceeded',
                    });
                    console.error('Reconnect exceeded');

                    if (config.onEvent) {
                        config.onEvent(PlayerStatus.Error);
                    }

                    return;
                }

                this.timer = setTimeout(async () => {
                    streamingLoggerStore.log({
                        type: LogType.AppDelivery,
                        message: 'StreamingProvider: CloudflareStreaming: Reconnect',
                    });
                    if (config.playUrl && this.player) {
                        await this.player.load(new URL(config.playUrl));
                    }
                }, RECONNECT_TIMEOUT);

                RECONNECTS -= 1;
                RECONNECT_TIMEOUT *= 2;
            });

            await this.player.load(new URL(config.playUrl));
        }
        if (config.muted) {
            this.player.mute();
        } else {
            this.player.unmute();
        }
        this.player.peer.addEventListener(
            'connectionstatechange',
            () => {
                streamingLoggerStore.log({
                    type: LogType.WebRTCDelivery,
                    message: 'StreamingProvider: CloudflareStreaming: player state changed'
                    + this.player?.peer?.connectionState,
                });
                if (this.player?.peer?.connectionState == 'connected') {
                    if (config.onEvent) {
                        config.onEvent(PlayerStatus.PeerConnectionEstablished);
                    }
                }
            }
        );
        this.player.peer.addEventListener(
            'icecandidateerror',
            (error) => {
                streamingLoggerStore.log({
                    type: LogType.AppBroadcast,
                    message: 'StreamingProvider: CloudflareStreaming: broadcast icecandidateerror',
                },
                { ...error },
                { includeExtendedData: true });
            }
        );
        this.monitorDeliveryStats();
        return this.player;
    }

    private monitorDeliveryStats() {
        let lastVideoBytesReceived = 0;
        let stalledCount = 0;
        const restartThreshold = 2;
        const checkInterval = 2_000;
        const peer = this.player?.peer;
        const monitor = async () => {
            console.log('Check');
            if (
                !peer ||
                peer.connectionState !== 'connected'
            ) {
                if (
                    !peer ||
                    ['new', 'connecting', 'connected']
                        .every((st) => peer?.connectionState !== st) ||
                    ['new', 'checking', 'connected', 'completed']
                        .every((st) => peer?.iceConnectionState !== st)
                ) {
                    console.log('RELOAD');
                }
                return;
            }
            try {
                const stats: RTCStatsReport = await peer.getStats();
                let videoReport: RTCInboundRtpStreamStats | undefined = undefined;
                stats.forEach((report) => {
                    if (report.type === 'inbound-rtp' && report.kind === 'video') {
                        videoReport = report as RTCInboundRtpStreamStats;
                    }
                });

                streamingLoggerStore.log({
                    type: LogType.AppDelivery,
                    message: `CloudFlare: player : videoReport: ${
                        videoReport ? JSON.stringify(videoReport) : undefined
                    }`,
                });

                if (videoReport) {
                    const videoStats = videoReport as RTCInboundRtpStreamStats;
                    const currentBytesReceived = videoStats.bytesReceived;
                    if (currentBytesReceived === undefined) {
                        return;
                    }
                    if (currentBytesReceived === lastVideoBytesReceived) {
                        stalledCount++;
                        streamingLoggerStore.log({
                            type: LogType.AppDelivery,
                            message:
                                `StreamingProvider: CloudflareStreaming: stalled player ${stalledCount} intervals`,
                        });
                    } else {
                        stalledCount = 0;
                    }
                    lastVideoBytesReceived = currentBytesReceived;
                }
            } catch (error) {
                console.error(error);
                streamingLoggerStore.log(
                    {
                        type: LogType.AppDelivery,
                        message: `StreamingProvider: CloudflareStreaming: error to get stats`,
                    },
                    { errorText: (error as Error).message },
                    { includeExtendedData: true }
                );
            }
        };

        this.deliveryCheckTimer = window.setInterval(monitor, checkInterval);
    }

    async stopDelivery() {
        if (this.timer) {
            clearTimeout(this.timer);
        }
        clearTimeout(this.deliveryCheckTimer);
        const player = this.player;
        if (player) {
            streamingLoggerStore.log({
                type: LogType.AppDelivery,
                message: 'StreamingProvider: CloudflareStreaming: stopDelivery',
            });
            player.mute();

            try {
                Emitter.emit(Events.STREAMING_STATS_WEBRTC_UNWATCH_PEER_CONNECTION, player.peer);
                Emitter.emit(
                    Events.STREAMING_STATS_WEBRTC_SEND_EVENT,
                    StreamingStatsEvents.stop_delivery,
                    { streamingType: 'delivery' }
                );

                await player.unload();
            } catch (e) {
                console.info(e);
                streamingLoggerStore.log(
                    {
                        type: LogType.AppDelivery,
                        message: 'StreamingProvider: CloudflareStreaming: stopDelivery Error',
                    },
                    { errorText: (e as Error).message },
                    { includeExtendedData: true }
                );
            }
            // eslint-disable-next-line
            // @ts-ignore
            player.removeAllListeners();
            this.stopCollectingsPlayerStats();
            this.player = undefined;
        }
    }

    attachBroadcastPeerConnectionEvents = (updateStatus: (event: StreamStatus | null) => void) => {
        if (!(this.broadcastPeerConnection instanceof RTCPeerConnection)) {
            return;
        }

        const callback = () => {
            switch (this.broadcastPeerConnection?.connectionState) {
                case 'new':
                    streamingLoggerStore.log({
                        type: LogType.AppBroadcast,
                        message: 'StreamingProvider: CloudflareStreaming: connectionState new',
                    });
                    updateStatus(StreamStatus.SocketOpened);

                    break;
                case 'connecting':
                    streamingLoggerStore.log({
                        type: LogType.AppBroadcast,
                        message: 'StreamingProvider: CloudflareStreaming: connectionState connecting',
                    });
                    updateStatus(StreamStatus.SocketOpened);

                    break;
                case 'connected':
                    streamingLoggerStore.log({
                        type: LogType.AppBroadcast,
                        message: 'StreamingProvider: CloudflareStreaming: connectionState connected',
                    });
                    updateStatus(StreamStatus.PeerConnectionEstablished);

                    break;
                case 'failed':
                    streamingLoggerStore.log({
                        type: LogType.AppBroadcast,
                        message: 'StreamingProvider: CloudflareStreaming: connectionState failed',
                    });
                    updateStatus(StreamStatus.Error);

                    break;
                case 'closed':
                    updateStatus(StreamStatus.Error);
                    break;
                default:
                    streamingLoggerStore.log({
                        type: LogType.AppBroadcast,
                        message: 'StreamingProvider: CloudflareStreaming: connectionState '
                        + this.broadcastPeerConnection?.connectionState,
                    });

                    updateStatus(null);

                    break;
            }
        };

        this.broadcastPeerConnection.addEventListener('connectionstatechange', callback);
        this.broadcastPeerConnection.addEventListener(
            'icecandidateerror',
            (error) => {
                streamingLoggerStore.log({
                    type: LogType.AppBroadcast,
                    message: 'StreamingProvider: CloudflareStreaming: broadcast icecandidateerror',
                },
                { ...error },
                { includeExtendedData: true });
            }
        );
        return () => this.broadcastPeerConnection?.removeEventListener('connectionstatechange', callback);
    };

    async destroy() {
        await this.stopDelivery();
        this.stopBroadcast();
    }
}
